import React, { useMemo, useRef, useState } from "react";

import InputRadioPillGroup from "../../../../components/InputRadioPillGroup/InputRadioPillGroup";

const SOC_GROUPS = [
	{ id: "soc-17", value: "17", label: "Architecture & Engineering" },
	{ id: "soc-27", value: "27", label: "Arts & Media" },
	{ id: "soc-37", value: "37", label: "Building & Grounds" },
	{ id: "soc-13", value: "13", label: "Business & Finance" },
	{ id: "soc-21", value: "21", label: "Community & Social Service" },
	{ id: "soc-15", value: "15", label: "Computer & Math" },
	{ id: "soc-47", value: "47", label: "Construction & Extraction" },
	{ id: "soc-25", value: "25", label: "Education & Library" },
	{ id: "soc-45", value: "45", label: "Farming & Forestry" },
	{ id: "soc-35", value: "35", label: "Food Preparation" },
	{
		id: "soc-healthcare-admin",
		value: "healthcare-admin",
		label: "Healthcare Admin",
		codes: ["43-6013", "29-2072", "11-9111", "43-4051", "43-3021"],
	},
	{ id: "soc-29", value: "29", label: "Healthcare Practitioners" },
	{ id: "soc-31", value: "31", label: "Healthcare Support" },
	{
		id: "soc-industrial",
		value: "industrial",
		label: "Industrial",
		codes: ["51-1011", "51-4041", "51-2092", "51-9061", "49-9041"],
	},
	{ id: "soc-49", value: "49", label: "Installation & Repair" },
	{ id: "soc-23", value: "23", label: "Legal" },
	{ id: "soc-19", value: "19", label: "Life & Social Science" },
	{ id: "soc-11", value: "11", label: "Management" },
	{ id: "soc-55", value: "55", label: "Military" },
	{ id: "soc-43", value: "43", label: "Office & Admin Support" },
	{ id: "soc-39", value: "39", label: "Personal Care" },
	{ id: "soc-51", value: "51", label: "Production" },
	{ id: "soc-33", value: "33", label: "Protective Service" },
	{
		id: "soc-retail",
		value: "retail",
		label: "Retail Workforce",
		codes: ["41-2031", "41-2011", "43-5081", "41-1011"],
	},
	{ id: "soc-41", value: "41", label: "Sales" },
	{
		id: "soc-software",
		value: "software",
		label: "Software & Technology",
		codes: ["15-1252", "15-1251", "15-1253", "15-1244", "15-1232"],
	},
	{ id: "soc-53", value: "53", label: "Transportation" },
	{
		id: "soc-transport-logistics",
		value: "transport-logistics",
		label: "Transportation & Logistics",
		codes: ["53-3032", "43-5032", "11-3071", "13-1081", "53-7051"],
	},
];

function AddSocCode({ socCodeList, selectedSocCode, handleToggleCheckboxSocCode, errorSOCValidation }) {
	const selectedSocCodeIds = selectedSocCode.map((soc) => soc.id);

	const [selectedGroup, setSelectedGroup] = useState(null);

	const [searchText, setSearchText] = useState("");

	const searchInputRef = useRef(null);

	const visibleCodes = useMemo(() => {
		if (!socCodeList) return [];

		if (selectedGroup) {
			const groupDefinition = SOC_GROUPS.find((g) => g.value === selectedGroup);

			if (groupDefinition?.codes) {
				return socCodeList.filter((code) => groupDefinition.codes.includes(code.soc_code));
			} else if (groupDefinition) {
				return socCodeList.filter((code) => code.soc_code.startsWith(selectedGroup + "-"));
			}
		}

		if (searchText) {
			const upperSearch = searchText.toUpperCase();
			return socCodeList.filter((code) => {
				const fullLabel = `${code.soc_code} ${code.soc_name}`.toUpperCase();
				return fullLabel.includes(upperSearch);
			});
		}

		return socCodeList;
	}, [socCodeList, selectedGroup, searchText]);

	const recursiveFunctionForRender = (codeList) => {
		return (
			codeList &&
			codeList.map((code, index) => (
				<div className='soc-code-list-item' key={index}>
					<label className='soc-code-list-title body-2'>
						{code.soc_code} {code.soc_name}
					</label>
					<input
						type='checkbox'
						className='accordion-check'
						checked={selectedSocCodeIds.includes(code.id)}
						onChange={() => {
							handleToggleCheckboxSocCode(code);
						}}
						name={code.soc_name}
						id={code.id}
					/>
				</div>
			))
		);
	};

	const handleSearchChange = (e) => {
		setSearchText(e.target.value);
		if (selectedGroup) {
			setSelectedGroup(null);
		}
	};

	const handlePillSelect = (value) => {
		setSelectedGroup(value);
		setSearchText("");
		if (searchInputRef.current) {
			searchInputRef.current.value = "";
			searchInputRef.current.classList.remove("hasFocus");
		}
	};

	const handlePillWrapperClick = (e) => {
		let clickedValue = null;

		if (e.target.type === "radio") {
			clickedValue = e.target.value;
		} else {
			const label = e.target.closest("label");
			if (label) {
				const nestedRadio = label.querySelector("input[type='radio']");
				if (nestedRadio) {
					clickedValue = nestedRadio.value;
				} else {
					const forId = label.getAttribute("for") || label.htmlFor;
					if (forId) {
						const radio = document.getElementById(forId);
						if (radio) clickedValue = radio.value;
					}
				}
			}
		}

		if (clickedValue && clickedValue === selectedGroup) {
			e.preventDefault();
			e.stopPropagation();
			setSelectedGroup(null);
		}
	};

	const handleFocus = (e) => {
		e.target.classList.add("hasFocus");
	};

	const handleBlur = (e) => {
		if (e.target.value === "") {
			e.target.classList.remove("hasFocus");
		}
	};

	return (
		<div className='db-state-table-modal-step-item step-three'>
			<div className='soc-code-wrapper'>
				<div className='soc-code-list-item-title'>
					<p>Labor Profiles (SOC)</p>
				</div>

				<div onClickCapture={handlePillWrapperClick}>
					<InputRadioPillGroup
						wrap={true}
						options={SOC_GROUPS}
						value={selectedGroup}
						onValueChange={handlePillSelect}
						inline={true}
					/>
				</div>

				<div className='select-search-input-wrap'>
					{errorSOCValidation && <span style={{ color: "red" }}>{errorSOCValidation}</span>}
					<div className='input-item'>
						<input
							type='text'
							id='search-soc'
							ref={searchInputRef}
							className='select-search-input'
							onKeyUp={handleSearchChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
							onClick={handleFocus}
							onChange={() => {}}
						/>
						<label htmlFor=''>Search Labor</label>
					</div>
				</div>
				<div className='soc-code-list-items' id='soc-code-list-items'>
					{recursiveFunctionForRender(visibleCodes)}
				</div>
			</div>
		</div>
	);
}

export default AddSocCode;
